import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import ResultSetCapture, { type ResultSetCapturePayload } from "../components/ResultSetCapture";
import { api, ApiError, type MyRefereeMatch, type MyRefereeMatches } from "../lib/api";
import { compactDateEs } from "../lib/dateFormat";
import { matchChipColor, matchChipLabel } from "../lib/referees";
import "./referees/referees.css";

// «Mis partidos de hoy» del árbitro (maqueta Arbitro-cel, Eduardo
// 2026-10-16): solo los partidos que le asignaron; captura por sets (la
// misma de jugadores y organizador) y W.O./RET/DEF, que siempre van a
// revisión del organizador.

type CaptureKind = "normal" | "walkover" | "retired" | "defaulted";

const KIND_LABELS: Array<{ value: CaptureKind; label: string }> = [
  { value: "normal", label: "Normal" },
  { value: "walkover", label: "W.O." },
  { value: "retired", label: "RET" },
  { value: "defaulted", label: "DEF" },
];

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function dayChip(date: string): string {
  const compact = compactDateEs(date);
  return compact ? `${compact.weekday} ${compact.day}` : date;
}

type MatchState = "published" | "pending" | "next" | "later";

function matchState(match: MyRefereeMatch, nextId: number | null): MatchState {
  if (match.has_result) return "published";
  if (match.my_report?.status === "pending") return "pending";
  return match.id === nextId ? "next" : "later";
}

const STATE_TAG: Record<MatchState, { text: string; className: string }> = {
  published: { text: "Publicado", className: "referee-tag referee-tag--done" },
  pending: { text: "Enviado · en revisión", className: "referee-tag referee-tag--pending" },
  next: { text: "Siguiente", className: "referee-tag referee-tag--next" },
  later: { text: "Más tarde", className: "referee-tag" },
};

export default function RefereeMatchesPage() {
  const { token } = useAuth();
  const [data, setData] = useState<MyRefereeMatches | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [kind, setKind] = useState<CaptureKind>("normal");
  const [absentId, setAbsentId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (day: string | null) => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await api.referee.myMatches(token, day);
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(errorText(err, "No se pudieron cargar tus partidos."));
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const matches = data?.matches ?? [];
  const nextId = useMemo(
    () => matches.find((m) => !m.has_result && m.my_report?.status !== "pending")?.id ?? null,
    [matches],
  );
  const sent = matches.filter((m) => m.my_report && m.my_report.status !== "rejected").length;
  const tournamentNames = (data?.tournaments ?? []).map((t) => t.name).join(" · ");
  const isToday = data?.day === todayIso();

  function openCapture(match: MyRefereeMatch) {
    setOpenId((current) => (current === match.id ? null : match.id));
    setKind("normal");
    setAbsentId(null);
    setMessage(null);
  }

  async function submit(match: MyRefereeMatch, payload: { score_text: string | null; winner_participant_id: number | null }, exception: CaptureKind) {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.referee.submit(token, match.id, {
        score_text: payload.score_text,
        winner_participant_id: payload.winner_participant_id,
        exception: exception === "normal" ? null : exception,
      });
      setMessage(
        response.data.status === "published"
          ? "Resultado publicado."
          : "Resultado enviado: el organizador lo revisa y lo publica.",
      );
      setOpenId(null);
      await load(data?.day ?? null);
    } catch (err) {
      setError(errorText(err, "No se pudo enviar el resultado."));
    } finally {
      setBusy(false);
    }
  }

  function renderCapture(match: MyRefereeMatch) {
    // Solo se captura con los dos jugadores ya definidos (el servidor lo exige).
    const player1 = { id: match.player1_participant_id ?? 0, name: match.player1_name };
    const player2 = { id: match.player2_participant_id ?? 0, name: match.player2_name };
    const reviewed = !match.referee_publishes_directly || kind !== "normal";
    const submitLabel = reviewed ? "Enviar al organizador" : "Publicar resultado";
    return (
      <section className="referee-capture" aria-label={`Capturar resultado de ${match.player1_name} vs ${match.player2_name}`}>
        <div className="referee-exceptions">
          EXCEPCIÓN
          <div className="referee-exceptions__row" role="radiogroup" aria-label="Excepción">
            {KIND_LABELS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={kind === option.value}
                className={`referees-filter${kind === option.value ? " is-current" : ""}`}
                onClick={() => {
                  setKind(option.value);
                  setAbsentId(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="referee-mode-note">
          {match.referee_publishes_directly ? (
            kind === "normal" ? (
              <>
                Este torneo: <strong>el árbitro publica directo</strong>. Se publica al guardar.
              </>
            ) : (
              <>
                W.O., RET y DEF <strong>siempre los revisa el organizador</strong> antes de publicarse.
              </>
            )
          ) : (
            <>
              Este torneo: <strong>el organizador revisa</strong>. Tu resultado le llega para publicar.
            </>
          )}
        </div>
        {kind === "walkover" ? (
          <div className="referee-wo">
            <strong>¿Quién no se presentó?</strong>
            <div className="referee-wo__options" role="radiogroup" aria-label="Quién no se presentó">
              {[player1, player2].map((player) => (
                <label key={player.id ?? player.name}>
                  <input
                    type="radio"
                    name={`wo-${match.id}`}
                    checked={absentId === player.id}
                    onChange={() => setAbsentId(player.id)}
                  />
                  {player.name}
                </label>
              ))}
            </div>
            <div className="referee-wo__actions">
              <button type="button" className="referees-btn referees-btn--ghost" onClick={() => setOpenId(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="referees-btn referees-btn--navy"
                disabled={busy || absentId === null}
                onClick={() =>
                  void submit(
                    match,
                    {
                      score_text: null,
                      winner_participant_id: absentId === player1.id ? player2.id : player1.id,
                    },
                    "walkover",
                  )
                }
              >
                Enviar al organizador
              </button>
            </div>
          </div>
        ) : (
          <ResultSetCapture
            key={`${match.id}-${kind}`}
            idPrefix={`arbitro-${match.id}`}
            player1={player1}
            player2={player2}
            pickleball={match.pickleball}
            initialScoreText={kind === "retired" ? "RET" : kind === "defaulted" ? "DEF" : null}
            busy={busy}
            showNotes={false}
            submitLabel={submitLabel}
            confirmLabel={reviewed ? "Sí, enviar" : "Sí, publicar"}
            confirmNote={reviewed ? "Le llega al organizador para revisar y publicar." : "Se publica en el torneo al guardar."}
            onSubmit={(payload: ResultSetCapturePayload) =>
              submit(match, { score_text: payload.score_text, winner_participant_id: payload.winner_participant_id }, kind)
            }
            onCancel={() => setOpenId(null)}
          />
        )}
      </section>
    );
  }

  return (
    <div className="referee-phone">
      <section className="referee-phone__hero">
        <span className="referee-phone__eyebrow">ÁRBITRO{tournamentNames ? ` · ${tournamentNames.toUpperCase()}` : ""}</span>
        <h1 className="referee-phone__title">
          {isToday || !data?.day ? "Mis partidos de hoy" : `Mis partidos · ${dayChip(data.day)}`}
        </h1>
        <div className="referee-phone__stats">
          <span className="referee-phone__stat">
            <strong>{matches.length}</strong>
            <span>asignados</span>
          </span>
          <span className="referee-phone__stat">
            <strong>{sent}</strong>
            <span>enviados</span>
          </span>
        </div>
      </section>
      <div className="referee-phone__lock">🔒 Solo ves los partidos que te asignaron.</div>
      {data && data.days.length > 1 ? (
        <div className="referee-phone__days" aria-label="Días">
          {data.days.map((item) => (
            <button
              key={item.date}
              type="button"
              className={`referees-filter${item.date === data.day ? " is-current" : ""}`}
              onClick={() => void load(item.date)}
            >
              {dayChip(item.date)} · {item.matches}
            </button>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="form-message" role="status">
          {message}
        </p>
      ) : null}
      {loading && !data ? <p className="muted">Cargando…</p> : null}
      {data && matches.length === 0 ? (
        <div className="referee-match">
          <strong>No tienes partidos asignados {isToday ? "hoy" : "este día"}.</strong>
          <span className="muted">Cuando el organizador te asigne partidos, aparecen aquí.</span>
        </div>
      ) : null}
      {matches.map((match) => {
        const state = matchState(match, nextId);
        const tag = STATE_TAG[state];
        const canCapture = state === "next" || state === "later" || state === "pending";
        return (
          <article key={match.id} className={`referee-match${state === "next" ? " is-next" : ""}`} data-referee-match={match.id}>
            <div className="referee-match__top">
              <div className="referee-match__when">
                <strong>{match.scheduled_time?.slice(0, 5) || "Sin hora"}</strong>
                <span>{match.court ? `Cancha ${match.court}` : "Cancha por definir"}</span>
                <span className="referees-group-chip" style={{ borderColor: matchChipColor(match), color: matchChipColor(match) }}>
                  {matchChipLabel(match)}
                </span>
              </div>
              <span className={tag.className}>{tag.text}</span>
            </div>
            {(data?.tournaments.length ?? 0) > 1 ? <span className="referee-match__tournament">{match.tournament_name}</span> : null}
            <strong>{match.player1_name}</strong>
            <span className="referee-match__vs">vs</span>
            <strong>{match.player2_name}</strong>
            {state === "published" && match.score_text ? <span className="muted">Resultado: {match.score_text}</span> : null}
            {canCapture && openId !== match.id ? (
              <button type="button" className="referees-btn referees-btn--navy" onClick={() => openCapture(match)}>
                {state === "pending" ? "Corregir lo que envié" : "Capturar resultado"}
              </button>
            ) : null}
            {openId === match.id ? renderCapture(match) : null}
          </article>
        );
      })}
    </div>
  );
}
